import React, { useEffect, useState } from "react"
import { IReplenishmentDto } from "../../../../../types/replenishment/IReplenishmentDto"
import { spacedNum } from "../../../../common/CommonHelper"
import { ICompanyBank, IFullCompanyDto } from "../../../../../types/company/IFullCompanyDto"
import * as TrinityAPI from "../../../../../requests/TrinityAPI"
import { IModalMessage } from "../../../../../types/error/IModalMessage"
import { ModalPopUp } from "../../../../common/element/ModalPopUp"
import { CommonInput } from "../../../../common/element/fields/CommonInput"
import { ModalConfirm } from "../../../../common/element/ModalConfirm"
import { CRWaitingBanner } from "../cr/CRWaitingBanner"
import { CRDone } from "../cr/CRDone"
import { ICalculateReplenishmentDto } from "../../../../../types/replenishment/ICalculateReplenishmentDto"
import {
  ICalculateReplenishmentRateType,
  ICalculateReplenishmentResponse
} from "../../../../../types/replenishment/ICalculateReplenishmentResponse"
import clsx from "clsx"

import style from "./style.module.scss"
import { useUpdateEffect } from "react-use"

export const CRRequestTab: React.FC<{
  replenishment: IReplenishmentDto
  setReplenishment: (r: IReplenishmentDto) => void
  stage: string
}> = ({ replenishment, setReplenishment, stage }) => {
  const [calculationRequestBefore, setCalculationRequestBefore] = useState<ICalculateReplenishmentDto>()
  const [calculationRequestAfter, setCalculationRequestAfter] = useState<ICalculateReplenishmentDto>()

  const [calculationResponseBefore, setCalculationResponseBefore] = useState<ICalculateReplenishmentResponse>()
  const [calculationResponseAfter, setCalculationResponseAfter] = useState<ICalculateReplenishmentResponse>()

  const [confirmBalance, setConfirmBalance] = useState<IModalMessage>()
  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [company, setCompany] = useState<IFullCompanyDto>()
  const [sending, setSending] = useState<boolean>(false)
  const [payerCompanyName, setPayerCompanyName] = useState<string>("")
  const [bankData, setBankData] = useState<ICompanyBank>()

  useEffect(() => {
    if (!replenishment.companyId) return

    TrinityAPI.getCompany(replenishment.companyId, response => {
      if (response.status === 200) {
        setCompany(response.data)
        setCalculationRequestBefore({
          ...calculationRequestBefore,
          companyId: response.data.id,
          replenishmentId: replenishment.id,
          clientRate: replenishment.clientRate,
          rate: replenishment.rateBefore,
          xRate: replenishment.xRateBefore,
          userId: replenishment.userId,
          invoiceAmount: replenishment.invoiceAmount
        })
        setCalculationRequestAfter({
          ...calculationRequestAfter,
          companyId: response.data.id,
          replenishmentId: replenishment.id,
          clientRate: replenishment.clientRate,
          rate: replenishment.rateAfter,
          xRate: replenishment.xRateAfter,
          userId: replenishment.userId,
          invoiceAmount: replenishment.invoiceAmount
        })
      } else {
        setModalMessage({
          title: "Ошибка!",
          text: `Ошибка получения компании: ${response.data.message}`
        })
      }
    })
  }, [replenishment.companyId])

  useEffect(() => {
    const { companyId, bankId } = replenishment

    if (companyId && bankId) {
      TrinityAPI.getCompanyBank(companyId, bankId, response => {
        if (response.status === 200) {
          setBankData(response.data)
        }
      })
    }
  }, [replenishment.companyId, replenishment.bankId])

  useEffect(() => {
    if (calculationRequestBefore?.rate && calculationRequestBefore.xRate) {
      TrinityAPI.calculateReplenishment(getCalculationRequestPrepared(calculationRequestBefore), "before", response => {
        if (response.status === 200) {
          setCalculationResponseBefore(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка отправки курса: ${response.data.message}`
          })
        }
      })
    }
  }, [calculationRequestBefore])

  useEffect(() => {
    if (calculationRequestAfter?.rate && calculationRequestAfter.xRate) {
      TrinityAPI.calculateReplenishment(getCalculationRequestPrepared(calculationRequestAfter), "after", response => {
        if (response.status === 200) {
          setCalculationResponseAfter(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка отправки курса: ${response.data.message}`
          })
        }
      })
    }
  }, [calculationRequestAfter])

  useEffect(() => {
    if (replenishment.clientRequestId && replenishment.status !== "NEW") {
      TrinityAPI.getClientRequest(Number(replenishment.clientRequestId), response => {
        if (response.status === 200) {
          setPayerCompanyName(response.data.clientCompanyName)
        }
      })
    }
  }, [replenishment.clientRequestId, replenishment.status])

  const getCalculationRequestPrepared = (source: ICalculateReplenishmentDto): ICalculateReplenishmentDto => {
    const prepared = source
    prepared.xRate = prepared.xRate?.toString().replaceAll(" ", "")
    prepared.rate = prepared.rate?.toString().replaceAll(" ", "")
    return prepared
  }

  const submitResponse = () => {
    if (sending) return
    if (!calculationResponseBefore || !calculationRequestBefore?.xRate) {
      setModalMessage({
        title: "Ошибка!",
        text: `Заполните курс USD/${replenishment.invoiceCurrency}`
      })
      return
    }
    if (!calculationRequestBefore?.rate) {
      setModalMessage({ title: "Ошибка!", text: `Заполните курс USD/RUB` })
      return
    }

    setSending(true)
    TrinityAPI.responseCR(getSubmitPrepared(), response => {
      if (response.status === 200) {
        window.location.reload()
      } else {
        setModalMessage({
          title: "Ошибка!",
          text: `Ошибка отправки курса: ${response.data.message}`
        })
        setSending(false)
      }
    })
  }

  const getSubmitPrepared = (): IReplenishmentDto => {
    const toSubmit = { ...replenishment }
    toSubmit.usdAmountBefore = calculationResponseBefore?.usdAmount?.replaceAll(" ", "")
    toSubmit.rubAmountBefore = calculationResponseBefore?.rubAmount?.replaceAll(" ", "")
    toSubmit.rateBefore = calculationRequestBefore?.rate?.toString().replaceAll(" ", "")
    toSubmit.xRateBefore = calculationRequestBefore?.xRate?.toString().replaceAll(" ", "")
    toSubmit.invoiceRateBefore = calculationResponseBefore?.invoiceRate?.replaceAll(" ", "")
    toSubmit.brockmanRateBefore = calculationResponseBefore?.brockmanRate?.replaceAll(" ", "")
    toSubmit.amountRate = String(calculationResponseBefore?.floatingRates.amountRate)
    toSubmit.volumeRate = String(calculationResponseBefore?.floatingRates.volumeRate)
    toSubmit.onTopAmount = String(calculationResponseBefore?.floatingRates.onTopAmount)

    return toSubmit
  }

  const getFillBalancePrepared = (): IReplenishmentDto => {
    const toSubmit = replenishment
    toSubmit.xRateAfter = calculationRequestAfter?.xRate?.toString().replaceAll(" ", "")
    toSubmit.rateAfter = calculationRequestAfter?.rate?.toString().replaceAll(" ", "")
    toSubmit.usdAmountAfter = calculationResponseAfter?.usdAmount?.replaceAll(" ", "")

    return toSubmit
  }

  const fillBalance = () => {
    if (sending) return
    setSending(true)
    TrinityAPI.crFixation(getFillBalancePrepared(), response => {
      if (response.status === 200) {
        window.location.reload()
      } else {
        setModalMessage({
          title: "Ошибка!",
          text: `Ошибка фиксации курса: ${response.data.message}`
        })
        setSending(false)
      }
    })
  }

  const confirmFillBalance = (e: boolean | undefined) => {
    if (e) fillBalance()
  }

  const validateFillBalance = () => {
    if (!calculationRequestAfter?.xRate || !calculationRequestAfter?.rate || !calculationResponseAfter?.usdAmount) {
      setModalMessage({ title: "Ошибка!", text: "Пожалуйста, заполните данные для расчетов" })
      return
    }
    setConfirmBalance({
      title: "Пополнить баланс?",
      text: `Подтверждаете пополнение баланса на сумму ${spacedNum(
        Number(calculationResponseAfter?.usdAmount) / 100
      )} USD`,
      callbackValue: true,
      _callback: confirmFillBalance
    })
  }

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (!replenishment?.id) return

      TrinityAPI.updateReplenishmentComment(replenishment.id, replenishment.comment || "")
    }, 400)

    return () => clearTimeout(timerId)
  }, [replenishment?.comment])

  if (replenishment.status === "DOC_ACC" || replenishment.status === "CR_NAC") {
    return <CRWaitingBanner status={replenishment.status} />
  } else {
    return (
      <>
        <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
        <ModalConfirm message={confirmBalance} setMessage={() => setConfirmBalance(undefined)} />
        <div className="container" style={{ maxWidth: "1600px" }}>
          <div className="container-pop-up">
            <ul className="form-left">
              <li className="form-block-popup">
                <h4 className="heading-chapter">Расчет</h4>
                <div
                  className="flex-row-10 flex-wrap-mobile"
                  style={
                    replenishment.status !== "CR_REQ"
                      ? {
                          pointerEvents: "none",
                          opacity: 0.7
                        }
                      : {}
                  }
                >
                  <fieldset>
                    <legend>Сумма</legend>
                    <p className="text-form">
                      {replenishment.invoiceAmount ? spacedNum(Number(replenishment.invoiceAmount) / 100) : 0}
                    </p>
                  </fieldset>
                  <fieldset className="fieldset-200">
                    <legend>Валюта</legend>
                    <p className="text-form">{replenishment.invoiceCurrency}</p>
                  </fieldset>
                </div>
                <div
                  className={style.rateTypes}
                  style={
                    replenishment.status !== "CR_REQ"
                      ? {
                          pointerEvents: "none",
                          opacity: 0.7
                        }
                      : {}
                  }
                >
                  <fieldset>
                    <legend>Базовая ставка</legend>
                    <p
                      className={clsx("text-form", {
                        "text-form--active":
                          stage === "CR_FIX"
                            ? calculationResponseAfter?.rateType === ICalculateReplenishmentRateType.Base
                            : calculationResponseBefore?.rateType === ICalculateReplenishmentRateType.Base
                      })}
                    >
                      {stage === "CR_FIX"
                        ? calculationResponseAfter?.baseRate || company?.baseRate
                        : calculationResponseBefore?.baseRate || company?.baseRate}
                      %
                    </p>
                  </fieldset>
                  <fieldset>
                    <legend>Ставка от суммы</legend>
                    <p
                      className={clsx("text-form", {
                        "text-form--active":
                          stage === "CR_FIX"
                            ? calculationResponseAfter?.rateType === ICalculateReplenishmentRateType.Amount
                            : calculationResponseBefore?.rateType === ICalculateReplenishmentRateType.Amount
                      })}
                    >
                      {stage === "CR_FIX"
                        ? calculationResponseAfter?.floatingRates.amountRate || replenishment.amountRate
                        : calculationResponseBefore?.floatingRates.amountRate || replenishment.amountRate}
                      %
                    </p>
                  </fieldset>
                  <fieldset>
                    <legend>Ставка от объема</legend>
                    <p
                      className={clsx("text-form", {
                        "text-form--active":
                          stage === "CR_FIX"
                            ? calculationResponseAfter?.rateType === ICalculateReplenishmentRateType.Volume
                            : calculationResponseBefore?.rateType === ICalculateReplenishmentRateType.Volume
                      })}
                    >
                      {stage === "CR_FIX"
                        ? calculationResponseAfter?.floatingRates.volumeRate || replenishment.volumeRate
                        : calculationResponseBefore?.floatingRates.volumeRate || replenishment.volumeRate}
                      %
                    </p>
                  </fieldset>
                  <fieldset>
                    <legend>Ставка клиента</legend>
                    <p
                      className={clsx("text-form", {
                        "text-form--active":
                          stage === "CR_FIX"
                            ? calculationResponseAfter?.rateType === ICalculateReplenishmentRateType.Client
                            : calculationResponseBefore?.rateType === ICalculateReplenishmentRateType.Client
                      })}
                    >
                      {replenishment.clientRate ? replenishment.clientRate + "%" : "Не заполнено"}
                    </p>
                  </fieldset>
                  <fieldset>
                    <legend>Индив. ставка</legend>
                    <p
                      className={clsx("text-form", {
                        "text-form--active":
                          stage === "CR_FIX"
                            ? calculationResponseAfter?.rateType === ICalculateReplenishmentRateType.Personal
                            : calculationResponseBefore?.rateType === ICalculateReplenishmentRateType.Personal
                      })}
                    >
                      {replenishment.personalRate ? replenishment.personalRate + "%" : "Не запрошена"}
                    </p>
                  </fieldset>
                </div>
                <div
                  className="flex-row-10 flex-wrap-mobile"
                  style={
                    replenishment.status !== "CR_REQ"
                      ? {
                          pointerEvents: "none",
                          opacity: 0.7
                        }
                      : {}
                  }
                >
                  <CommonInput
                    typeName={"number"}
                    label={`Курс USD/${replenishment.invoiceCurrency}`}
                    placeholder={"Введите курс"}
                    name={"xRateBefore"}
                    value={calculationRequestBefore?.xRate || replenishment.xRateBefore}
                    errorModel={undefined}
                    _callback={v => setCalculationRequestBefore({ ...calculationRequestBefore, xRate: v })}
                  />
                  <CommonInput
                    typeName={"number"}
                    label={"Курс USD/RUB"}
                    placeholder={"Введите курс для расчета"}
                    name={"rateBefore"}
                    value={calculationRequestBefore?.rate || replenishment.rateBefore}
                    errorModel={undefined}
                    _callback={v => setCalculationRequestBefore({ ...calculationRequestBefore, rate: v })}
                  />
                </div>

                {replenishment.status === "CR_FIX" || replenishment.status === "DONE" ? (
                  <div
                    className="flex-row-10"
                    style={
                      replenishment.status === "DONE"
                        ? {
                            pointerEvents: "none",
                            opacity: 0.7
                          }
                        : {}
                    }
                  >
                    <CommonInput
                      typeName={"number"}
                      label={`Курс USD/${replenishment.invoiceCurrency} (фиксация)`}
                      placeholder={"Введите курс"}
                      name={"xRateAfter"}
                      value={calculationRequestAfter?.xRate || replenishment.xRateAfter}
                      errorModel={undefined}
                      _callback={v => setCalculationRequestAfter({ ...calculationRequestAfter, xRate: v })}
                    />
                    <CommonInput
                      typeName={"number"}
                      label={"Курс USD/RUB (фиксация)"}
                      placeholder={"Введите курс после фиксации"}
                      name={"rateAfter"}
                      value={calculationRequestAfter?.rate || replenishment.rateAfter}
                      errorModel={undefined}
                      _callback={v => setCalculationRequestAfter({ ...calculationRequestAfter, rate: v })}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </li>
              <div className={clsx("form-block-popup", style.payerNameWrapper)}>
                <div>
                  <h4 className="heading-chapter">Валютный плательщик: {company?.name}</h4>
                  <div className={style.payerBankInfo}>
                    <div>
                      <b>SWIFT:</b> {bankData?.swift}
                    </div>
                    <div>
                      <b>Номер:</b> {bankData?.accountNumber}
                    </div>
                    <div>
                      <b>Валюта:</b> {bankData?.currency}
                    </div>
                  </div>
                </div>
                <fieldset>
                  <legend>Название компании плательщика</legend>
                  <p className="text-form">{payerCompanyName}</p>
                </fieldset>
              </div>

              {stage === "CR_FIX" && (
                <div className="form-block-popup">
                  <h4 className="heading-chapter">Комментарий</h4>
                  <div className="flex-column-5">
                    <input
                      type="text"
                      className="input-form"
                      placeholder="Введите комментарий"
                      onChange={e => setReplenishment({ ...replenishment, comment: e.target.value })}
                      value={replenishment?.comment}
                      maxLength={50}
                    />
                  </div>
                </div>
              )}
            </ul>
            <div className="right-form">
              <div className="form-block-popup">
                <h4 className="heading-chapter">
                  {replenishment.status === "DONE" ? "Окончательный" : "Предварительный"} расчет в рублях
                </h4>
                <div className="flex-row-10 flex-wrap-mobile">
                  <fieldset>
                    <legend className="legend-no-padding">Сумма в USD</legend>
                    <p className="text-form-final">
                      {spacedNum(Number(calculationResponseBefore?.usdAmount || replenishment.usdAmountBefore) / 100)}
                    </p>
                  </fieldset>
                  <fieldset>
                    <legend className="legend-no-padding">Сумма в RUB</legend>
                    <p className="text-form-final">
                      {spacedNum(Number(calculationResponseBefore?.rubAmount || replenishment.rubAmountBefore) / 100)}
                    </p>
                  </fieldset>
                </div>
                {replenishment.status === "CR_AWT" && (
                  <p className="message-course-no">
                    Клиент пока не согласовал курс. Возможно, он вернется к этому позже
                  </p>
                )}
              </div>
              {replenishment.status === "CR_FIX" || replenishment.status === "DONE" ? (
                <div className="form-block-popup">
                  <h4 className="heading-chapter">
                    {replenishment.status === "DONE" ? "Окончательный" : "Предварительный"} расчет в рублях после
                    фиксации
                  </h4>
                  <div className="flex-row-10 flex-wrap-mobile">
                    <fieldset>
                      <legend className="legend-no-padding">Сумма к зачислению USD</legend>
                      <p className="text-form-final">
                        {calculationResponseAfter?.usdAmount
                          ? spacedNum(Number(calculationResponseAfter?.usdAmount) / 100)
                          : "-"}
                      </p>
                    </fieldset>
                    <fieldset>
                      <legend className="legend-no-padding">Сумма в {replenishment.invoiceCurrency}</legend>
                      <p className="text-form-final">
                        {calculationResponseAfter?.invoiceAmount
                          ? spacedNum(Number(calculationResponseAfter.invoiceAmount) / 100)
                          : "-"}
                      </p>
                    </fieldset>
                  </div>
                  <div className="flex-row-10">
                    <fieldset>
                      <legend className="legend-no-padding">Поступило средств</legend>
                      <p className="text-form-final">
                        {replenishment.rubAmountAfter ? spacedNum(Number(replenishment.rubAmountAfter) / 100) : "-"}
                      </p>
                    </fieldset>
                  </div>
                  {replenishment.status === "DONE" && (
                    <p className="message-course-yes">Курс зафиксирован, указанная сумма зачислена на счет клиента</p>
                  )}
                </div>
              ) : (
                <></>
              )}
            </div>

            {replenishment.status === "CR_REQ" && (
              <div className="flex-row-10">
                <button className="btn" onClick={submitResponse}>
                  {sending ? "Подождите..." : "Отправить"}
                </button>
              </div>
            )}
            {replenishment.status === "CR_FIX" && (
              <div className="flex-row-10">
                <button className="btn" onClick={validateFillBalance}>
                  {sending ? "Подождите..." : "Зачислить"}
                </button>
              </div>
            )}

            {/*DOWNLOADABLE ITEMS IN DONE STATUS*/}
            <CRDone replenishment={replenishment} />
          </div>
        </div>
      </>
    )
  }
}
